import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import api, { authApi } from "../api/api";
import { User } from "../types/models";

interface AuthContextType {
  user: User | null;
  token: string | null;
  roles: string[];
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const normalizeNames = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.map((value) => String(value).trim().replace(/\s+/g, "_"));
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch current user data
  const fetchUser = async (): Promise<void> => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    try {
      // Auth endpoints are at /api level, not /api/v1
      const response = await authApi.get("/me");
      
      const authData = response.data?.data || response.data;
      if (authData) {
        const { user, roles = [], permissions = [] } = authData;
        
        if (user) {
          setUser(user);
          setRoles(normalizeNames(roles));
          setPermissions(normalizeNames(permissions));
          setToken(storedToken);
        } else {
          throw new Error("User data not found in response");
        }
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error: any) {
      console.error("Failed to fetch user:", error);
      // Clear invalid token
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      setRoles([]);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    try {
      // Auth endpoints are at /api level, not /api/v1
      const response = await authApi.post("/login", { email, password });
      
      // Handle different response structures
      const responseToken = response.data?.data?.token || response.data?.token;
      
      if (!responseToken) {
        throw new Error("No token received from server");
      }

      // Store token
      localStorage.setItem("token", responseToken);
      setToken(responseToken);

      // Fetch user data
      await fetchUser();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Login failed";
      throw new Error(errorMessage);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      // Auth endpoints are at /api level, not /api/v1
      await authApi.post("/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local state regardless of API response
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      setRoles([]);
      setPermissions([]);
    }
  };

  // Fetch user on mount if token exists
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Update API interceptor when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  const value: AuthContextType = {
    user,
    token,
    roles,
    permissions,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    fetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
